import { ApiError, apiFetch, apiRequest, getAccessToken, resolveApiBase } from './client';
const BASE = '/pdf-layouts';
// Allowed patientBlock field keys (schema v1 whitelist). Order in a config's
// fields array is render order; a key omitted from the array is hidden.
export const PDF_LAYOUT_PATIENT_FIELD_KEYS = [
    'patientName',
    'species',
    'breed',
    'patientAge',
    'ownerName',
    'studyDescription',
    'modality',
    'studyDate',
    'accessionNumber',
    'referringVet',
    'institution',
];
// Body-block keys for the ordered `blocks` list (Phase 9). Header/footer are
// fixed page furniture; the default order is exactly this array. "sections"
// is required; a key absent from a config's blocks = that block hidden.
export const PDF_LAYOUT_BLOCK_KEYS = [
    'patientBlock',
    'request',
    'sections',
    'images',
    'diagnoses',
    'signature',
];
function ownerPath(kind, ownerId) {
    return `${BASE}/${kind}/${encodeURIComponent(ownerId)}`;
}
/** The owner's saved layout, or the built-in default (builtIn: true). */
export async function getPdfLayout(kind, ownerId) {
    return await apiFetch(ownerPath(kind, ownerId));
}
/** Upsert the owner's DEFAULT layout's config (admin-gated server-side; bumps version). */
export async function putPdfLayout(kind, ownerId, config) {
    return await apiRequest('PUT', ownerPath(kind, ownerId), { config });
}
/**
 * Every named layout the owner has, default first then name ASC (the server's
 * `{ layouts }` envelope is unwrapped here). An owner with no rows yet gets a
 * single built-in pseudo-entry (id null, builtIn true) so pickers always have
 * one row.
 */
export async function listPdfLayouts(kind, ownerId) {
    const response = await apiFetch(`${ownerPath(kind, ownerId)}/all`);
    return response.layouts;
}
/**
 * Create a named layout seeded with `config`. The owner's first-ever row
 * becomes the default automatically. A duplicate name (case-insensitive)
 * surfaces as an ApiError with status 409.
 */
export async function createPdfLayout(kind, ownerId, name, config) {
    return await apiRequest('POST', ownerPath(kind, ownerId), { name, config });
}
/**
 * Update a named row: `config` and/or `name` (omitted fields untouched;
 * version bumps only when config changes). Rename collisions are 409; a
 * layoutId that does not belong to this owner is 404.
 */
export async function updatePdfLayoutById(kind, ownerId, layoutId, data) {
    return await apiRequest('PUT', `${ownerPath(kind, ownerId)}/${encodeURIComponent(layoutId)}`, data);
}
/** Atomically make this row the owner's default (clears the previous default). */
export async function setDefaultPdfLayout(kind, ownerId, layoutId) {
    return await apiRequest('POST', `${ownerPath(kind, ownerId)}/${encodeURIComponent(layoutId)}/set-default`);
}
/**
 * Delete a named non-default row (deleting the default is a 400: set another
 * design as default first). Connection references clear server-side.
 */
export async function deletePdfLayout(kind, ownerId, layoutId) {
    await apiRequest('DELETE', `${ownerPath(kind, ownerId)}/${encodeURIComponent(layoutId)}`);
}
/**
 * Render a canned sample report through the real generator with the supplied
 * (not-necessarily-saved) config. Returns a Blob so the caller drives the
 * URL.createObjectURL lifecycle, mirroring the report pdf-preview fetchers.
 * format 'image' returns a PNG of page one — used for the live preview
 * because <img> renders in every browser while iframe-embedded PDFs depend
 * on the browser's PDF plugin; 'pdf' (default) is for download.
 */
export async function postPdfLayoutPreview(kind, ownerId, config, format = 'pdf') {
    const endpoint = `${ownerPath(kind, ownerId)}/preview`;
    const token = getAccessToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const query = format === 'image' ? '?format=image' : '';
    const response = await fetch(`${resolveApiBase(endpoint)}${endpoint}${query}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ config }),
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'Layout preview failed'));
    }
    return await response.blob();
}
